import {
  type MutableRefObject,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useState,
} from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { computeContextMenuGates } from '../helpers/context-menu-gates'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

type ObjectContextMenuState = {
  open: boolean
  position: { x: number; y: number }
  mode: 'selection' | 'canvas'
  targetShapeId?: string
  selectedShapeIds: string[]
  isPhotoSelection: boolean
}

/** The state an opened object/canvas context menu starts from — one shape for both open routes. */
function buildOpenObjectContextMenu(
  position: { x: number; y: number },
  mode: ObjectContextMenuState['mode'],
  targetShapeId: string | undefined,
  selectedShapeIds: string[],
  isPhotoSelection: boolean
): ObjectContextMenuState {
  return { open: true, position, mode, targetShapeId, selectedShapeIds, isPhotoSelection }
}

/**
 * Context menus (shapes + empty canvas, and the inline shape-text edit menu): open/close state,
 * the open handlers, and the menus' show/enable gates read off the live selection during render.
 */
export function useContextMenus({
  internalShapeManagerRef,
  linkedTextChains,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  linkedTextChains: CenterCanvasConfig['linkedTextChains']
}) {
  // Context menu (shapes + empty canvas)
  const [objectContextMenu, setObjectContextMenu] = useState<ObjectContextMenuState>({
    open: false,
    position: { x: 0, y: 0 },
    mode: 'selection',
    selectedShapeIds: [],
    isPhotoSelection: false,
  })

  const [shapeTextEditContextMenu, setShapeTextEditContextMenu] = useState<{
    open: boolean
    position: { x: number; y: number }
    isCollapsed: boolean
  }>({
    open: false,
    position: { x: 0, y: 0 },
    isCollapsed: true,
  })

  const handleShapeContextMenu = useCallback(
    (
      shapeIds: string[],
      e: ReactMouseEvent,
      meta?: { targetLocked?: boolean; targetShapeId?: string }
    ) => {
      if (shapeIds.length === 0) return

      const resolvedTargetId =
        meta?.targetShapeId ?? (shapeIds.length === 1 ? shapeIds[0] : undefined)
      const targetShape = resolvedTargetId
        ? internalShapeManagerRef.current?.getShapes()?.find((s) => s.id === resolvedTargetId)
        : undefined
      const isPhotoSelection = shapeIds.length === 1 && !!targetShape?.photoFill

      setObjectContextMenu(
        buildOpenObjectContextMenu(
          { x: e.clientX, y: e.clientY },
          'selection',
          meta?.targetShapeId,
          shapeIds,
          isPhotoSelection
        )
      )
    },
    [internalShapeManagerRef]
  )

  const handleEmptyCanvasContextMenu = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    const { target } = event

    if (target instanceof Element) {
      if (target.closest('[data-shape-id]')) return
      // Selection chrome and the rest of the visual layer (lock/freeze badges, hover outline, drag
      // rects, smart guides) are screen-space siblings painted ABOVE the canvas and carry no
      // `data-shape-id`, so a chrome hit that its own handler misses lands here and opens the
      // canvas BACKGROUND menu on top of a selected object (WMPCM-9825). This guard used to name
      // `data-unified-selection-chrome`, an attribute of the in-canvas chrome deleted in the
      // visual-layer migration, so it has matched nothing since — `data-visual-layer` is the live
      // wrapper (see the VisualLayer host in `components/screen-space-layers.tsx`). A dead
      // `data-textbox-id` check went out with it for the same reason: a text frame is a
      // `data-shape-id` shape since text-unification.
      if (target.closest('[data-visual-layer]')) return
    }

    event.preventDefault()
    event.stopPropagation()

    setObjectContextMenu(
      buildOpenObjectContextMenu(
        { x: event.clientX, y: event.clientY },
        'canvas',
        undefined,
        [],
        false
      )
    )
  }, [])

  const handleObjectContextMenuClose = useCallback(() => {
    setObjectContextMenu((prev) => ({ ...prev, open: false }))
    setShapeTextEditContextMenu((prev) => ({ ...prev, open: false }))
  }, [])

  const handleShapeTextContextMenu = useCallback(
    (payload: { clientX: number; clientY: number; isSelectionCollapsed: boolean }) => {
      setObjectContextMenu((prev) => ({ ...prev, open: false }))
      setShapeTextEditContextMenu({
        open: true,
        position: { x: payload.clientX, y: payload.clientY },
        isCollapsed: payload.isSelectionCollapsed,
      })
    },
    []
  )

  const contextMenuShapes = internalShapeManagerRef.current?.getShapes() ?? []
  const contextMenuSelectedShapeIds = internalShapeManagerRef.current?.getSelectedShapeIds() ?? []
  const contextMenuGates = computeContextMenuGates({
    contextMenuShapes,
    contextMenuSelectedShapeIds,
    objectContextMenu,
    linkedTextChains,
  })

  return {
    objectContextMenu,
    shapeTextEditContextMenu,
    setShapeTextEditContextMenu,
    handleShapeContextMenu,
    handleEmptyCanvasContextMenu,
    handleObjectContextMenuClose,
    handleShapeTextContextMenu,
    ...contextMenuGates,
  }
}
